import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Trash2, Merge } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MasterTable from "@/components/MasterTable";

export default function ClientsVendorsPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [keepId, setKeepId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/clients-vendors", { params: { limit: 500 } }); setItems(data || []); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name}?`)) return;
    try { await api.delete(`/clients-vendors/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openMerge = () => {
    if (selected.size < 2) return toast.error("Pick at least 2 rows to merge");
    setKeepId(""); setMergeOpen(true);
  };

  const doMerge = async () => {
    if (!keepId) return toast.error("Pick which row to keep");
    const merge_ids = Array.from(selected).filter(id => id !== keepId);
    if (merge_ids.length === 0) return toast.error("Pick at least one row to fold in");
    setBusy(true);
    try {
      const { data } = await api.post("/clients-vendors/merge", { keep_id: keepId, merge_ids });
      toast.success(`Merged ${data.merged_count} row(s) into ${data.kept_name} · total hits: ${data.total_hits}`);
      setMergeOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setBusy(false); }
  };

  const selectedRows = items.filter(x => selected.has(x.id));

  return (
    <div className="p-8 space-y-6" data-testid="clients-vendors-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Users size={12}/> Client / Vendor master</div>
          <h1 className="text-4xl font-display mt-1">Clients & Vendors.</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-populated from Inward Senders + Outward Recipients. Tick two or more rows to <b>merge duplicates</b> into one canonical record.
          </p>
        </div>
        <Button onClick={openMerge} disabled={selected.size < 2} data-testid="cv-merge-btn"
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Merge size={13} className="mr-1.5" /> Merge selected ({selected.size})
        </Button>
      </header>

      <MasterTable
        rows={items}
        columns={[
          { key: "_sel", label: "", sortable: false, render: r => (
              <input type="checkbox" data-testid={`cv-check-${r.id}`}
                     checked={selected.has(r.id)} onChange={() => toggle(r.id)}
                     className="h-4 w-4 accent-primary cursor-pointer" />
          )},
          { key: "name", label: "Name", render: r => <span className="font-semibold">{r.name}</span> },
          { key: "kinds", label: "Used as", render: r => (r.kinds || []).map(k => (
              <span key={k} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-border mr-1">{k}</span>
          ))},
          { key: "phone", label: "Phone", className: "mono text-xs" },
          { key: "email", label: "Email", className: "text-xs text-muted-foreground" },
          { key: "hit_count", label: "Used", className: "mono text-right", render: r => <span className="mono">{r.hit_count || 0}</span> },
          { key: "actions", label: "", sortable: false, render: r => (
              <button onClick={() => remove(r)} data-testid={`cv-del-${r.id}`}
                      className="text-destructive hover:text-primary"><Trash2 size={13}/></button>
          )},
        ]}
        searchKeys={["name", "phone", "email"]}
        testidPrefix="cv"
        defaultSort={{ key: "hit_count", dir: "desc" }}
        emptyText="No entries yet. Save an Inward/Outward parcel to auto-populate."
      />

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="cv-merge-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Merge duplicates</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-muted-foreground">
              Pick the row to <b>keep</b>. The others will fold into it — <span className="mono">hit_count</span> sums, <span className="mono">kinds</span> merge, and any missing phone/email/address fill from the folded rows.
            </div>
            <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-y-auto">
              {selectedRows.map(r => (
                <label key={r.id} className={`flex items-center justify-between p-2 cursor-pointer ${keepId === r.id ? "bg-emerald-50" : "hover:bg-muted/30"}`}
                       data-testid={`cv-keep-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="keep" checked={keepId === r.id} onChange={() => setKeepId(r.id)}
                           className="h-4 w-4 accent-primary" />
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-[10px] text-muted-foreground">{(r.kinds || []).join(", ")} · hits {r.hit_count || 0}</div>
                    </div>
                  </div>
                  {keepId === r.id && <span className="text-[9px] font-bold uppercase text-emerald-700">KEEP</span>}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMergeOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={doMerge} disabled={busy || !keepId} data-testid="cv-merge-confirm"
                      className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
                {busy ? "Merging…" : "Merge now"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
